import { SectionShell } from "../components/SectionShell";
import { Button } from "../components/Button";
import { DecorativeBlob } from "../components/DecorativeBlob";
import { cn } from "../utils";
import type { HTMLAttributes } from "react";

export interface CtaBarSectionProps extends HTMLAttributes<HTMLElement> {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  primaryCta: { label: string; href: string };
  secondaryCta?: { label: string; href: string };
}

/**
 * Compact CTA band with gradient/surface treatment.
 */
export function CtaBarSection({
  eyebrow,
  title,
  subtitle,
  primaryCta,
  secondaryCta,
  className,
  ...props
}: CtaBarSectionProps) {
  return (
    <SectionShell
      className={cn("py-10 md:py-12 relative overflow-hidden", className)}
      backgroundClassName="bg-gradient-to-r from-primary/10 via-accent/10 to-secondary/10"
      {...props}
    >
      {/* Animated background decorations */}
      <DecorativeBlob variant="primary" size="lg" position="top-0 left-[10%]" animation="float" />
      <DecorativeBlob variant="secondary" size="md" position="bottom-0 right-[15%]" animation="pulse-glow" />

      <div className="relative z-10 bg-[var(--color-surface,#fff)]/90 border border-neutral-200 dark:border-white/10 shadow-sm-2 rounded-[16px] p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="space-y-1">
          {eyebrow && (
            <div className="text-xs font-semibold uppercase tracking-wide text-primary">
              {eyebrow}
            </div>
          )}
          <h3 className="text-xl md:text-2xl font-bold" style={{ color: "var(--color-text,#0f172a)" }}>
            {title}
          </h3>
          {subtitle && (
            <p className="text-sm md:text-base" style={{ color: "var(--color-text,#0f172a)", opacity: 0.8 }}>
              {subtitle}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-3 md:justify-end">
          <Button asChild variant="primary">
            <a href={primaryCta.href}>{primaryCta.label}</a>
          </Button>
          {secondaryCta && (
            <Button asChild variant="ghost">
              <a href={secondaryCta.href}>{secondaryCta.label}</a>
            </Button>
          )}
        </div>
      </div>
    </SectionShell>
  );
}
